import React, { useState, useEffect, useMemo, lazy, Suspense, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Spinner, Alert, Row, Col } from 'reactstrap';

import * as Yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { toast } from 'react-toastify';
import merge from 'lodash/merge';
import assign from 'lodash/assign';

//redux
import { useSelector, useDispatch } from 'react-redux';

import { useLoading } from '../../../../Context/LoadingContext';
import 'dayjs/locale/es-mx';
import { decodeSecureHash, validatePrefillData } from '../utils/hashUtils';
import FormLoadingScreen from '../Components/FormLoadingScreen';

// PDF Preview - Lazy loaded para reducir bundle inicial
const PdfPreviewModal = lazy(() => import('../../../../Components/General/Modal/PdfPreviewModal'));
import useDtePdfPolling from '../../../../Components/Hooks/useDtePdfPolling';

// Thunks
import { createDTE } from '../../../../slices/warehouseGuide/thunk';
import { deliverWarehouseItemStockCheck } from '../../../../slices/warehouseItem/thunk';
import { getEstablishmentList } from '../../../../slices/establishment/thunk';
import { getWarehouseList } from '../../../../slices/warehouse/thunk';
import { StockCheckErrorModal } from './StockCheckErrorModal';
import { GuideCategoryBar, GuideCategoryBarSpec } from './GuideCategoryBar';
import { OrigenSection, OrigenSectionSpec } from './OrigenSection';
import { DestinoSection, DestinoSectionSpec } from './DestinoSection';
import { TransporteSection, TransporteSectionSpec } from './TransporteSection';
import { ItemsSection, ItemsSectionSpec } from './ItemsSection';
import { TotalesSection, TotalesSectionSpec } from './TotalesSection';
import {
	ObservacionesSection,
	ObservacionesSectionSpec,
} from './ObservacionesSection';

// Residuos sections
import {
	ResiduosDestinoSection,
	ResiduosDestinoSectionSpec,
} from './residuos/ResiduosDestinoSection';

// Form Dictionary Context
import { FormDictionaryProvider, useFormDictionary } from './FormDictionaryContext';
import { normalizeFacelePlainText } from '../../../../helpers/dte/facelePlainTextNormalize';

// Workflow Router
import { WorkflowRouter } from '../workflows';
import { rollbackWarGuide } from '../workflows/rollback';
import { getFieldLabel } from '../constants/guideConstants';

/**
 * Formatea errores de Formik a un array de objetos { label, message }
 */
const formatValidationErrors = (errors) => {
	const formatError = (key, value) => {
		if (typeof value === 'string') {
			return [{ label: getFieldLabel(key), message: value }];
		}
		if (Array.isArray(value)) {
			// Errores en arrays (como items)
			return value.flatMap((item, index) => {
				if (typeof item === 'object' && item !== null) {
					return Object.entries(item).map(([subKey, subValue]) => ({
						label: `${getFieldLabel(key)} #${index + 1} - ${getFieldLabel(subKey)}`,
						message: typeof subValue === 'string' ? subValue : JSON.stringify(subValue)
					}));
				}
				return [{ label: `${getFieldLabel(key)} #${index + 1}`, message: item }];
			});
		}
		if (typeof value === 'object' && value !== null) {
			// Errores anidados
			return Object.entries(value).flatMap(([subKey, subValue]) =>
				formatError(`${key}.${subKey}`, subValue)
			);
		}
		return [{ label: getFieldLabel(key), message: JSON.stringify(value) }];
	};

	return Object.entries(errors).flatMap(([key, value]) => formatError(key, value));
};

// TODO [extensibilidad]: getSectionSpecs y el ternario de destino (línea ~950) podrían
// leer la sección destino desde WorkflowRouter.getSectionComponent(category) en vez de
// if/else hardcodeado. El submit flow (stock-check EPP, rollback residuos) podría
// delegarse al workflow module con hooks tipo preSubmit / onRollback.
const getSectionSpecs = guideCategory => {
	const baseSpecs = [
		GuideCategoryBarSpec,
		OrigenSectionSpec,
		TransporteSectionSpec,
		ItemsSectionSpec, // ItemsSection siempre presente, se adapta internamente
		ObservacionesSectionSpec,
		TotalesSectionSpec,
	];

	if (guideCategory === 'epp') {
		return [
			...baseSpecs.slice(0, 2), // GuideCategoryBar, Origen
			DestinoSectionSpec,
			...baseSpecs.slice(2), // Transporte, Items, Observaciones, Totales
		];
	} else if (guideCategory === 'residuos') {
		return [
			...baseSpecs.slice(0, 2), // GuideCategoryBar, Origen
			ResiduosDestinoSectionSpec,
			...baseSpecs.slice(2), // Transporte, Items, Observaciones, Totales
		];
	}

	// Default: solo base specs (sin destino específico)
	return baseSpecs;
};

// 1) initialValues: deep-merge de todas las sections + drivers globales
function mergeInitialValues(specs) {
	const list = specs.map(s =>
		s.getInitialValues ? s.getInitialValues() : {}
	);
	// Drivers “ocultos”/derivados para validaciones condicionales entre sections
	const drivers = {
		establishment_type: '', // OrigenSection debe setearlo con setFieldValue(...)
	};
	return merge({}, drivers, ...list);
}

// 2) validationSchema: unir shapes de cada section (usar .when entre campos)
function buildValidationSchema(specs) {
	const fields = specs.reduce((acc, s) => {
		const shape = s.getValidationSchema ? s.getValidationSchema() : null;
		return assign(acc, (shape && shape.fields) || {});
	}, {});
	const schema = Yup.object(fields);
	return schema;
}

// 3) payload: deep-merge de cada section
function buildPayload(values, specs) {
	const parts = specs.map(s => (s.toPayload ? s.toPayload(values) : {}));
	return merge({}, ...parts);
}

const sanitizeRUTs = payload => {
	// iterate deep over payload keys that includes "RUT" in the key removing dots/spaces
	// and preserving hyphens. Returns a new sanitized structure without mutating input.
	const sanitizeValueIfRUTKey = (key, value) => {
		if (typeof value === 'string' && /rut/i.test(key)) {
			return value.replace(/\./g, '').replace(/\s+/g, '').toUpperCase();
		}
		return value;
	};

	const recurse = node => {
		if (Array.isArray(node)) {
			return node.map(item => recurse(item));
		}
		if (node && typeof node === 'object') {
			const result = {};
			Object.entries(node).forEach(([key, val]) => {
				if (val && typeof val === 'object') {
					result[key] = recurse(val);
				} else {
					result[key] = sanitizeValueIfRUTKey(key, val);
				}
			});
			return result;
		}
		return node;
	};

	return recurse(payload);
};

/**
 * Sanitiza el payload convirtiendo todos los strings a mayúsculas,
 * excepto campos específicos que deben preservarse (fechas, RUTs, IDs, numéricos).
 * Antes: normalización Facele (sin acentos, m³→m3, etc.) para alinear con el PDF y evitar doble-wrap raro.
 */
const sanitizeUppercase = payload => {
	// Keys que NO deben convertirse a mayúsculas
	const EXCLUDED_KEYS = [
		// RUTs (ya normalizados por sanitizeRUTs)
		/rut/i,
		// Fechas
		/fecha/i,
		/date/i,
		/fch/i,
		// IDs técnicos
		/_id$/i,
		/^id$/i,
		// Campos numéricos (keys que terminan en Item o son puramente numéricos)
		/qtyitem/i,
		/prcitem/i,
		/montoitem/i,
		/mntneto/i,
		/mntexe/i,
		/mnttotal/i,
		/iva/i,
		/tasaiva/i,
		// Códigos técnicos (generalmente ya son alfanuméricos específicos)
		/vlrcodigo/i,
		/tpocodigo/i,
	];

	const shouldExclude = key => EXCLUDED_KEYS.some(pattern => pattern.test(key));

	const recurse = node => {
		if (Array.isArray(node)) {
			return node.map(item => recurse(item));
		}
		if (node && typeof node === 'object') {
			const result = {};
			Object.entries(node).forEach(([key, val]) => {
				if (val && typeof val === 'object') {
					result[key] = recurse(val);
				} else if (typeof val === 'string') {
					const plain = normalizeFacelePlainText(val);
					result[key] = shouldExclude(key) ? plain : plain.toUpperCase();
				} else {
					result[key] = val;
				}
			});
			return result;
		}
		return node;
	};

	return recurse(payload);
};

const GuideForm = props => {
	const {
		isEdit = false,
		handleToggleModal,
		handleChangeTitleForm = null,
		requireGuideDetailRegisterList = false,
		activeTab,
		initialCategory = '', // NUEVO: categoría inicial desde query params
	} = props;


	// Construir initialValues con TODAS las secciones posibles (EPP + Residuos)
	// para evitar reinicialización del formulario
	const initialValues = useMemo(() => {
		const allSpecs = [
			GuideCategoryBarSpec,
			OrigenSectionSpec,
			DestinoSectionSpec,
			ResiduosDestinoSectionSpec,
			TransporteSectionSpec,
			ItemsSectionSpec, // ItemsSection maneja tanto EPP como Residuos
			ObservacionesSectionSpec,
			TotalesSectionSpec,
		];
		const merged = mergeInitialValues(allSpecs);
		// Si hay categoría inicial, setearla en los valores iniciales
		if (initialCategory) {
			merged.guide_category = initialCategory;
		}
		return merged;
	}, [initialCategory]); // Solo depende de initialCategory, no cambia después
	
	// El validation schema se construye dinámicamente basado en la categoría actual del formulario
	// NOTA: Esto se recalcula cuando cambia initialCategory (prefill) o cuando el usuario cambia categoría
	const [currentCategory, setCurrentCategory] = useState(initialCategory);
	
	const validationSchema = useMemo(() => {
		const specs = getSectionSpecs(currentCategory);
		return buildValidationSchema(specs);
	}, [currentCategory]);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const { 
		openLoading, 
		closeLoading, 
		openSteps, 
		updateStep, 
		setPhase, 
		setSummaryMessage, 
		addSteps, 
		setActionButtons 
	} = useLoading();
	const [isPdfOpen, setIsPdfOpen] = useState(false);
	const [dteId, setDteId] = useState('');
	const [pdfReadyToView, setPdfReadyToView] = useState(false);

	// Estado para validación visual de errores
	const [validationErrors, setValidationErrors] = useState(null);

	// Estado para modal de errores de stock
	const [stockErrorModal, setStockErrorModal] = useState({
		isOpen: false,
		message: '',
		errors: [],
	});
	const {
		start: startPdfPolling,
		status: pdfStatus,
		blob: pdfBlob,
		error: pdfError,
		stop: stopPdfPolling,
	} = useDtePdfPolling();

	// Selector para DTE
	const { isCreatingDTE, isCreatedDTE, isCreateDTEError } = useSelector(
		state => state.WarehouseGuide
	);

	// Hook para acceder al diccionario de datos del formulario
	const { mastersReady, areMastersReady, isDictionaryLoading, loadDictionaryData } =
		useFormDictionary();

	// Estado para controlar cuando mostrar el contenido del formulario
	const [isFormReady, setIsFormReady] = useState(false);

	// Estado para procesamiento de hash de pre-llenado
	const [prefillData, setPrefillData] = useState(null);
	const [hashError, setHashError] = useState(null);

	// Refs para trackear estado sin causar re-renders
	const hasLoadedDictionary = useRef(false);
	const hasProcessedHash = useRef(false);

	// Extraer hash de URL - memoizado para no cambiar en cada render
	const dataHash = useMemo(() => {
		const queryParams = new URLSearchParams(window.location.search);
		return queryParams.get('prefill');
	}, []);

	// DEFINIR useFormik ANTES de los efectos que lo usan
	const validation = useFormik({
		enableReinitialize: false, // NO reinicializar cuando cambian valores
		initialValues,
		validationSchema,
		validateOnChange: true,
		validateOnBlur: true,
		validateOnMount: false,
		onSubmit: async values => {
			setIsLoading(true);
			setValidationErrors(null); // Limpiar errores previos

			// ============================================
			// CONSTRUIR LISTA DE PASOS COMPLETA
			// ============================================
			const needsStockCheck =
				values.guide_category === 'epp' &&
				values.epp_operation_type === 'retiro';

			const allSteps = [
				// Condicional: solo si es retiro EPP
				...(needsStockCheck
					? [{ id: 'stock_check', label: 'Verificando stock SAP' }]
					: []),
				// Siempre presente
				{ id: 'create_dte', label: 'Creando documento tributario (DTE)' },
				// Pasos del workflow (dinámicos por categoría)
				...(values.guide_category && WorkflowRouter.hasWorkflow(values.guide_category)
					? WorkflowRouter.getProgressSteps(values.guide_category, values)
					: []),
				// Siempre al final
				{ id: 'preview', label: 'Preparando vista previa PDF' },
			];

			openSteps(allSteps);

			try {
				// Validar el workflow antes de emitir el DTE para evitar folios huérfanos.
				if (
					values.guide_category &&
					WorkflowRouter.hasWorkflow(values.guide_category)
				) {
					const preValidation = await WorkflowRouter.preValidate(
						values.guide_category,
						values
					);

					if (!preValidation.valid) {
						const message = preValidation.errors.join(', ');
						const firstWorkflowStep = allSteps.find(s => s.id.startsWith('wf_'));

						updateStep('create_dte', 'warning', 'No se emitió DTE por validación previa');
						if (firstWorkflowStep) {
							updateStep(firstWorkflowStep.id, 'warning', message);
						}

						setPhase('aborted');
						setSummaryMessage(
							`Operación cancelada antes de emitir DTE: ${message}`
						);
						setIsLoading(false);
						return;
					}
				}

				// ============================================
				// PASO 1: STOCK CHECK (solo EPP Retiro)
				// ============================================
				if (needsStockCheck) {
					updateStep('stock_check', 'loading');

					const ppeIds = [
						...new Set(
							values.items
								?.flatMap(item => {
									const moduleVars = item._module_vars || {};
									if (
										Array.isArray(
											moduleVars.personal_protective_equipment_ids
										)
									) {
										return moduleVars.personal_protective_equipment_ids;
									}
									return moduleVars.personal_protective_equipment_id
										? [moduleVars.personal_protective_equipment_id]
										: [];
								})
								.filter(Boolean)
						),
					];

					if (ppeIds?.length > 0) {
						try {
							await dispatch(
								deliverWarehouseItemStockCheck({
									personal_protective_equipment_ids: ppeIds,
								})
							).unwrap();
							updateStep('stock_check', 'done');
						} catch (stockError) {
							updateStep('stock_check', 'error', 'Stock insuficiente en SAP');

							// Parsear error
							let errorData = stockError;
							if (typeof stockError === 'string') {
								try {
									errorData = JSON.parse(stockError);
								} catch {
									errorData = { message: stockError };
								}
							}

							// Mostrar modal de error SIN resetear el formulario
							setStockErrorModal({
								isOpen: true,
								message:
									errorData?.message ||
									errorData?.detail?.message ||
									'Error verificando stock en SAP',
								errors:
									errorData?.errors || errorData?.detail?.errors || [],
							});

							setIsLoading(false);
							setPhase('aborted');
							setSummaryMessage('Operación cancelada: stock insuficiente en SAP.');
							return;
						}
					}
				}

				// ============================================
				// PASO 2: CREAR DTE
				// ============================================
				updateStep('create_dte', 'loading');

				let dteResult, newDteId, folio;
				
				try {
					const currentSpecs = getSectionSpecs(values.guide_category);
					const payload = buildPayload(values, currentSpecs);

					const baseDte = {
						dtegbusiness: null,
						dtedbnet: null,
						dtefacele: {
							Encabezado: {
								IdDoc: {
									TipoDTE: 52, // Guía de despacho
									Folio: 0,
								},
							},
						},
					};

					const dtePayload = merge({}, baseDte, payload);
					const sanitizedDtePayload = sanitizeUppercase(
						sanitizeRUTs(dtePayload),
					);

					dteResult = await dispatch(
						createDTE({
							dte: sanitizedDtePayload,
							service: 'facele',
						})
					).unwrap();

					newDteId = dteResult?.data?._id;
					folio = dteResult?.data?.folio || '';

					if (!newDteId) {
						updateStep('create_dte', 'error', 'No se recibió ID del DTE');
						setPhase('failed');
						setSummaryMessage('No se pudo crear el DTE correctamente.');
						setIsLoading(false);
						return;
					}

					updateStep('create_dte', 'done');
				} catch (dteError) {
					updateStep('create_dte', 'error', dteError.message || 'Error al crear DTE');
					setPhase('aborted');
					setSummaryMessage(
						`Error al crear el DTE: ${dteError.message || dteError}`
					);
					setIsLoading(false);
					return;
				}

				// ============================================
				// PASO 3: EJECUTAR WORKFLOW POST-DTE
				// ============================================
				if (
					values.guide_category &&
					WorkflowRouter.hasWorkflow(values.guide_category)
				) {
					// Callback para reportar progreso
					const onProgress = (stepId, status, message) => {
						updateStep(stepId, status, message);
					};

					// Ejecutar el workflow
					try {
						const workflowResult = await WorkflowRouter.execute(
							dispatch,
							values.guide_category,
							dteResult,
							values,
							onProgress
						);

						// Workflow completado exitosamente
						updateStep('preview', 'loading');
						setPhase('completed');
						setSummaryMessage(`Guía generada exitosamente con folio ${folio}.`);
						setDteId(newDteId);
						startPdfPolling(newDteId);
					} catch (workflowError) {
						console.error('[GuideForm] Error en workflow:', workflowError);

						// ============================================
						// MANEJO DE ERROR POST-DTE: ROLLBACK
						// ============================================
						const createdResources = workflowError.createdResources || {};
						const { warGuideId, resGuideId, documentId, folio: wfFolio } = createdResources;

						// Detectar si es workflow de envío desde bodega
						const isWarehouseSend = values.residuos_operation_type?.startsWith('envio_bodega');

						// Cambiar fase a rollback
						setPhase('rollback');
						const errorMessage = workflowError.message || String(workflowError);
						setSummaryMessage(
							`Error en el workflow: ${errorMessage}. Iniciando anulación...`
						);

						// Inyectar pasos de rollback específicos según el tipo de operación
						const rollbackSteps = WorkflowRouter.getRollbackSteps(values.guide_category, values);
						addSteps(rollbackSteps);

						// Intentar rollback: primero `war_guide` (listado unificado), luego `res_guide` en residue
						updateStep('rb_annul_guide', 'loading');

						let rollbackWarOk = true;
						let rollbackResOk = true;

						if (warGuideId) {
							try {
								await rollbackWarGuide(
									warGuideId,
									`Rollback automático: ${errorMessage}`
								);
							} catch (rollbackError) {
								console.error('[GuideForm] Error en rollback war_guide:', rollbackError);
								rollbackWarOk = false;
								updateStep(
									'rb_annul_guide',
									'error',
									rollbackError.message || String(rollbackError)
								);
							}
						}

						if (isWarehouseSend && resGuideId) {
							try {
								const { rollbackWarehouseSend } = await import(
									'../workflows/rollback/warehouseSendRollback'
								);

								const rb = await rollbackWarehouseSend(
									dispatch,
									resGuideId,
									`Rollback automático: ${errorMessage}`,
									values.residuos_operation_type,
								);
								if (!rb.success) {
									rollbackResOk = false;
									updateStep(
										'rb_annul_guide',
										'error',
										rb.message || 'Error al anular guía de residuos'
									);
								}
							} catch (rollbackError) {
								console.error(
									'[GuideForm] Error en rollback guía residue:',
									rollbackError
								);
								rollbackResOk = false;
								updateStep(
									'rb_annul_guide',
									'error',
									rollbackError.message || String(rollbackError)
								);
							}
						}

						const hadRollbackTarget =
							!!warGuideId || (isWarehouseSend && !!resGuideId);

						const anyRollbackFailed =
							(warGuideId && !rollbackWarOk) ||
							(isWarehouseSend && resGuideId && !rollbackResOk);

						if (hadRollbackTarget && !anyRollbackFailed) {
							updateStep(
								'rb_annul_guide',
								'done',
								warGuideId && isWarehouseSend && resGuideId
									? 'Listado unificado y guía de residuos anulados'
									: warGuideId
										? 'Guía interna (listado unificado) anulada correctamente'
										: 'Guía de residuos anulada correctamente'
							);
						} else if (!hadRollbackTarget) {
							updateStep(
								'rb_annul_guide',
								'skipped',
								'No hay guía interna ni guía de residuos para anular automáticamente'
							);
						}

						// Fase final: failed
						setPhase('failed');

						// Construir mensaje detallado de lo que quedó creado
						const createdItems = [];
						if (folio || wfFolio) createdItems.push(`DTE con folio ${folio || wfFolio}`);
						if (documentId) createdItems.push(`documento ${documentId}`);
						if (resGuideId) createdItems.push(`guía de residuos ID ${resGuideId}`);
						if (warGuideId) createdItems.push(`guía listado unificado ID ${warGuideId}`);

						const createdItemsText = createdItems.length > 0
							? `Se crearon: ${createdItems.join(', ')}. `
							: '';

						const rollbackAttempted =
							!!warGuideId || (isWarehouseSend && !!resGuideId);
						const rollbackResultText =
							rollbackAttempted && !anyRollbackFailed
								? 'Se intentó anular automáticamente lo creado en el flujo.'
								: rollbackAttempted
									? isWarehouseSend && resGuideId && !rollbackResOk
										? 'La anulación automática falló. Revise el stock RESPEL en bodega y la guía de residuos manualmente.'
										: 'La anulación automática falló en al menos un paso; revise manualmente.'
									: 'No se ejecutó anulación automática (no aplica a este error).';

						setSummaryMessage(
							`Error en el proceso: ${errorMessage}. ${createdItemsText}${rollbackResultText} Debe revisar manualmente estos recursos en el sistema.`
						);
						setIsLoading(false);
						return; // Importante: no seguir al catch principal
					}
				} else {
					// Sin workflow, solo DTE
					updateStep('preview', 'loading');
					setPhase('completed');
					setSummaryMessage(`Guía registrada exitosamente con folio ${folio}.`);
					setDteId(newDteId);
					startPdfPolling(newDteId);
				}
			} catch (error) {
				// Error general no capturado (no debería llegar aquí normalmente)
				console.error('[GuideForm] Error general no manejado:', error);
				
				// Marcar todos los pasos pendientes como skipped
				allSteps.forEach(step => {
					if (step.status === 'pending' || step.status === 'loading') {
						updateStep(step.id, 'skipped');
					}
				});
				
				setPhase('aborted');
				setSummaryMessage(
					`Error inesperado al procesar la guía: ${error.message || error}`
				);
			} finally {
				setIsLoading(false);
			}
		},
	});

	// ============================================
	// EFECTOS DE INICIALIZACIÓN - Después de useFormik
	// ============================================

	// Efecto 1: Cargar diccionario una sola vez al montar
	useEffect(() => {
		if (hasLoadedDictionary.current) return;
		hasLoadedDictionary.current = true;
		
		loadDictionaryData(dispatch, getEstablishmentList, getWarehouseList).catch(err => {
			console.error('[GuideForm] Error cargando diccionario:', err);
			setHashError(err.message);
		});
	}, [dispatch, loadDictionaryData]);

	// Efecto 2: Procesar hash cuando los maestros estén listos
	// NOTA: Usamos ref para acceder a validation sin incluirlo en dependencias
	const validationRef = useRef(validation);
	useEffect(() => {
		validationRef.current = validation;
	});

	useEffect(() => {
		// Solo procesar si hay hash y no se ha procesado
		if (!dataHash || hasProcessedHash.current || prefillData || hashError) return;
		
		// Verificar que los maestros necesarios estén cargados
		const requiredMasters = ['establishments', 'warehouses'];
		if (!areMastersReady(requiredMasters)) {
			return; // Esperar a que los maestros estén listos
		}

		// Verificar que validation esté listo (usando ref para no depender de validation)
		if (!validationRef.current || !validationRef.current.setFieldValue) {
			return;
		}

		// Marcar como procesado para evitar re-ejecuciones
		hasProcessedHash.current = true;

		const processHash = async () => {
			try {
				// Decodificar y validar
				const decoded = decodeSecureHash(dataHash);
				validatePrefillData(decoded);

				// Aplicar al formulario
				applyPrefillDataToForm(decoded, validationRef.current);

				// Guardar los datos prefill
				setPrefillData(decoded);

				// Mostrar notificación PRIMERO
				toast.info(
					'Datos de envío desde bodega cargados. Complete los campos de destino y transporte.',
					{ autoClose: 5000 }
				);

				// Pequeño delay para permitir que todos los re-renders terminen
				// antes de mostrar el formulario (evita que el usuario vea los parpadeos)
				setTimeout(() => {
					setIsFormReady(true);
				}, 300); // 300ms es suficiente para que los useEffects de OSelect terminen
			} catch (error) {
				console.error('[GuideForm] Error procesando hash:', error);
				setHashError(error.message);
				setIsFormReady(true); // Mostrar formulario aunque haya error (para que usuario pueda continuar manualmente)
				toast.error(`Error al cargar datos pre-llenados: ${error.message}`, {
					autoClose: 5000,
				});
			}
		};

		processHash();
	}, [dataHash, mastersReady, areMastersReady, isDictionaryLoading, prefillData, hashError]);

	// Efecto 3: Marcar formulario como listo cuando no hay hash
	useEffect(() => {
		if (!dataHash && !isFormReady && !isDictionaryLoading) {
			setIsFormReady(true);
		}
	}, [dataHash, isFormReady, isDictionaryLoading]);

	// ============================================
	// OTROS EFECTOS
	// ============================================

	// Sincronizar currentCategory con guide_category del formulario
	// Solo cuando el valor realmente cambia y es diferente del estado actual
	useEffect(() => {
		const formCategory = validation.values.guide_category;
		if (formCategory && formCategory !== currentCategory) {
			setCurrentCategory(formCategory);
		}
	}, [validation.values.guide_category, currentCategory]);

	// Cierre de spinner global y manejo de timeout/error del PDF
	useEffect(() => {
		if (pdfStatus === 'ready') {
			updateStep('preview', 'done');
			setPdfReadyToView(true);
			
			// Configurar botones de acción: "Ver PDF" y "Cerrar"
			setActionButtons([
				{
					label: 'Ver PDF',
					icon: 'ri-file-pdf-line',
					color: 'primary',
					onClick: () => {
						setIsPdfOpen(true);
					}
				},
				{
					label: 'Cerrar',
					icon: 'ri-close-line',
					color: 'success',
					onClick: () => {
						// Cerrar modal
						closeLoading();
						setPdfReadyToView(false);
						
						// Resetear formulario completamente
						validation.resetForm();
						
						// Limpiar estados adicionales
						setDteId('');
						stopPdfPolling();
						
						// Opcional: mostrar mensaje de confirmación
						toast.success('Formulario reseteado. Puede crear una nueva guía.', {
							autoClose: 2000
						});
					}
				}
			]);
		}
		if (pdfStatus === 'timeout') {
			updateStep('preview', 'warning', 'Timeout esperando PDF');
			setSummaryMessage('El PDF aún no está listo. Por favor, revise más tarde en el listado de guías.');
		}
		if (pdfStatus === 'error') {
			updateStep('preview', 'error', 'Error al obtener PDF');
			setSummaryMessage('Ocurrió un error al obtener el PDF, pero la guía fue registrada correctamente.');
		}
	}, [pdfStatus]);

	useEffect(() => {
		if (handleChangeTitleForm != null) {
			handleChangeTitleForm('Registrar Guía de Despacho');
		}
	}, []);

	// Función para aplicar datos pre-llenados al formulario
	// Usa setValues para actualizar todos los campos en un solo batch (evita re-renders múltiples)
	const applyPrefillDataToForm = (data, validation) => {
		const valuesToSet = {};

		// 1. Setear categoría y tipo de operación
		valuesToSet.guide_category = data.category;
		valuesToSet.residuos_operation_type = data.residuos_operation_type;
		valuesToSet.residue_type_id = data.residue_type_id;

		// 2. Setear datos de origen
		if (data.origen) {
			valuesToSet.establishment_id = data.origen.establishment_id;
			valuesToSet.center_origin = data.origen.center_origin;
			valuesToSet.company_name = data.origen.company_name;
			valuesToSet.company_rut = data.origen.company_rut;
			valuesToSet.establishment_address = data.origen.establishment_address;
			valuesToSet.establishment_commune = data.origen.establishment_commune;
			valuesToSet.company_business_activity = data.origen.company_business_activity;
			valuesToSet.company_business_activity_code = data.origen.company_business_activity_code;
			valuesToSet.siep_code_origin = data.origen.siep_code_origin;
		}

		// 3. Setear items (guide_details convertidos)
		if (data.items && data.items.length > 0) {
			valuesToSet.items = data.items;
		}

		// 4. Setear fecha actual si no viene
		if (!validation.values.guide_date) {
			valuesToSet.guide_date = new Date();
		}

		// Aplicar todos los valores en un solo batch (un solo re-render)
		validation.setValues(prevValues => ({
			...prevValues,
			...valuesToSet,
		}));
	};

	return (
		<React.Fragment>
			<FormDictionaryProvider>
				{/* Overlay de loading SOLO cuando hay prefill - nunca desmontamos FormikProvider */}
				{dataHash && (!isFormReady || isDictionaryLoading || (!prefillData && !hashError)) && (
					<div
						className='container-fluid p-4'
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 10,
							backgroundColor: 'rgba(255,255,255,0.95)'
						}}
					>
						<FormLoadingScreen
							message='Preparando formulario de envío desde bodega...'
							submessage='Cargando establecimientos, bodegas y datos de residuos seleccionados...'
						/>
					</div>
				)}

				{/* Overlay de error de hash */}
				{hashError && (
					<div
						className='container-fluid p-4'
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 11,
							backgroundColor: 'rgba(255,255,255,0.95)'
						}}
					>
						<Alert color='danger'>
							<h5>Error al cargar datos pre-llenados</h5>
							<p>{hashError}</p>
							<Button color='primary' onClick={() => window.location.reload()}>
								Reintentar
							</Button>
						</Alert>
					</div>
				)}

				{/* FormikProvider siempre montado - evita ciclos de re-render */}
				<FormikProvider value={validation}>
					<Form
					className='tablelist-form'
					style={{ opacity: !dataHash || isFormReady ? 1 : 0 }}
					onSubmit={async e => {
						e.preventDefault();

						// Validar manualmente antes de enviar
						const validationResult = await validation.validateForm();

						if (Object.keys(validationResult).length > 0) {
							// Formatear errores para mostrar al usuario
							const formattedErrors = formatValidationErrors(validationResult);
							setValidationErrors(formattedErrors);

							// Marcar todos los campos con error como touched
							const touchedFields = {};
							Object.keys(validationResult).forEach(field => {
								touchedFields[field] = true;
							});
							validation.setTouched(touchedFields);

							// Scroll al top para que el usuario vea el alert
							window.scrollTo({ top: 0, behavior: 'smooth' });

							return false;
						}

						validation.handleSubmit();
						return false;
					}}
				>
					<div>
						{/* Barra compacta de categoría - controla el flujo */}
						<GuideCategoryBar />

						<Row className='g-3 align-items-start'>
							<Col lg={12}>
								<OrigenSection />
							</Col>
							
							{/* Sección de Destino - SIEMPRE visible, contenido dinámico según categoría */}
							<Col lg={6}>
								{validation.values.guide_category === 'residuos' ? (
									<ResiduosDestinoSection />
								) : (
									<DestinoSection />
								)}
							</Col>
							
							<Col lg={6}>
								<TransporteSection />
							</Col>
							
							{/* Sección de Items - SIEMPRE visible, se adapta según categoría */}
							<Col lg={12}>
								<ItemsSection />
							</Col>
							
							<Col lg={12}>
								<ObservacionesSection />
							</Col>
						</Row>

						{/* Totales - card pequeño alineado a la derecha */}
						<div className='d-flex justify-content-end mt-3'>
							<div style={{ maxWidth: '320px', minWidth: '280px' }}>
								<TotalesSection />
							</div>
						</div>

						{/* Estado del proceso - usando ternario para evitar renderizado de valores falsy */}
						{isLoading ? (
							<Alert color='info' fade={false}>
								<i className='ri-loader-2-line me-2'></i>
								Procesando guía de despacho...
							</Alert>
						) : null}

						{/* Errores de validación - usando ternario para evitar renderizado de valores falsy */}
						{validationErrors?.length > 0 ? (
							<Alert color='danger' className='mt-3'>
								<h5 className='alert-heading'>
									<i className='ri-error-warning-line me-2'></i>
									Campos requeridos faltantes
								</h5>
								<p className='mb-2'>Por favor, complete los siguientes campos antes de continuar:</p>
								<ul className='mb-0'>
									{validationErrors.map((error, idx) => (
										<li key={idx}>
											<strong>{error.label}:</strong> {error.message}
										</li>
									))}
								</ul>
							</Alert>
						) : null}

						{/* Botones de Acción */}
						<div className='hstack gap-2 justify-content-end mt-4'>
							<Button
								type='button'
								onClick={() => {
									handleToggleModal();
								}}
								className='btn-danger btn-label'
							>
								<i className='icon-x label-icon'></i>
								Cancelar
							</Button>
							<button
								type='submit'
								className='btn btn-label btn-primary'
								id='add-btn'
								disabled={isLoading || isCreatingDTE}
							>
								{isLoading || isCreatingDTE ? (
									<i className='label-icon'>
										<Spinner size='sm' />
									</i>
								) : (
									<i className='ri-save-3-line label-icon'></i>
								)}
								Registrar Guía
							</button>
						</div>
					</div>
					</Form>
				</FormikProvider>
			</FormDictionaryProvider>
			{/* PDF Preview Modal - Lazy loaded con Suspense */}
			<Suspense fallback={<div className="d-none" />}>  
				<PdfPreviewModal
					isOpen={isPdfOpen && pdfStatus === 'ready'}
					onClose={() => {
						setIsPdfOpen(false);
						stopPdfPolling();
					}}
					blob={pdfBlob}
					title={`Guía ${dteId}`}
				/>
			</Suspense>

			{/* Modal de errores de Stock Check SAP */}
			<StockCheckErrorModal
				isOpen={stockErrorModal.isOpen}
				onClose={() =>
					setStockErrorModal({ isOpen: false, message: '', errors: [] })
				}
				errorMessage={stockErrorModal.message}
				errorDetails={stockErrorModal.errors}
			/>
		</React.Fragment>
	);
};

export default GuideForm;
